import { CommonModule } from '@angular/common';
import { Component, effect, input, OnDestroy, output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import {
  NuMonacoEditorComponent,
  NuMonacoEditorEvent,
} from '@ng-util/monaco-editor';

@Component({
  selector: 'app-code-editor',
  template: `
    <div class="h-full w-full">
      <nu-monaco-editor
        #editor
        [options]="options"
        [height]="'100%'"
        [placeholder]=""
        [autoFormat]="true"
        (event)="onEditorEvent($event)" />
    </div>
  `,
  imports: [CommonModule, FormsModule, NuMonacoEditorComponent],
})
export class CodeEditor implements OnDestroy {
  private editorInstance: monaco.editor.IStandaloneCodeEditor | null = null;
  private debounceTimer: number | null = null;
  private readonly DEBOUNCE_DELAY = 1000; // 1000ms debounce delay
  private enableAutoSave = false;

  codeInput = input<string>(''); // default code to display in the editor
  language = input<string>('javascript'); // default language for the editor
  codeOutput = output<string>(); // current code in the editor
  readOnly = input<boolean>(false);

  // https://microsoft.github.io/monaco-editor/typedoc/variables/editor.EditorOptions.html
  options = {
    theme: 'vs-dark',
    language: 'javascript',
    scrollBeyondLastLine: false,
    minimap: {
      enabled: false,
    },
    fontSize: 16,
    readOnly: false,
    tabSize: 2,
    formatOnType: true,
    formatOnPaste: true,
  };

  constructor() {
    effect(() => {
      const language = this.language();
      const code = this.codeInput();
      const readOnly = this.readOnly();
      this.updateEditor(code, language, readOnly);
    });
  }

  private updateEditor(
    code: string,
    language: string,
    readOnly: boolean
  ): void {
    if (!this.editorInstance) return;
    const currentModel = this.editorInstance.getModel();
    if (!currentModel) return;

    const currentLanguage = currentModel.getLanguageId();
    if (currentLanguage !== language) {
      monaco.editor.setModelLanguage(currentModel, language);
    }

    const currentCode = this.editorInstance.getValue();
    if (currentCode !== code) {
      this.editorInstance.setValue(code);
    }

    this.editorInstance.updateOptions({ readOnly });
    this.editorInstance.getAction('editor.action.formatDocument')!.run();
  }

  private emitCodeWithDebounce(code: string): void {
    // Clear existing timer
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }

    // Set new timer
    this.debounceTimer = window.setTimeout(() => {
      // console.log('Content changed (debounced)\n----------\n', code);
      this.codeOutput.emit(code);
      this.debounceTimer = null;
    }, this.DEBOUNCE_DELAY);
  }

  // https://microsoft.github.io/monaco-editor/typedoc/interfaces/editor.IStandaloneCodeEditor.html
  onEditorEvent(event: NuMonacoEditorEvent) {
    if (event.type === 'init' && event.editor) {
      // init
      this.editorInstance = event.editor as monaco.editor.IStandaloneCodeEditor;
      this.updateEditor(this.codeInput(), this.language(), this.readOnly());

      this.editorInstance.onDidBlurEditorText(() => {
        this.enableAutoSave = false;
      });

      this.editorInstance.onDidFocusEditorWidget(() => {
        this.enableAutoSave = true;
      });

      // content-change listener with debounce
      this.editorInstance.onDidChangeModelContent(() => {
        if (!this.enableAutoSave || !this.editorInstance) return;
        const code = this.editorInstance.getValue();
        this.emitCodeWithDebounce(this.normalizeCode(code));
      });
    }
  }

  getCode(): string {
    if (!this.editorInstance) return '';
    const code = this.editorInstance.getValue();
    this.codeOutput.emit(this.normalizeCode(code));
    return this.normalizeCode(code);
  }

  private normalizeCode(code: string): string {
    return code.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
  }

  ngOnDestroy(): void {
    // Clean up debounce timer
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = null;
    }

    // Dispose of editor instance
    if (this.editorInstance) {
      this.editorInstance.dispose();
      this.editorInstance = null;
    }
  }
}
